import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { carrierUrls } from '@constants/urls';
import { AtgLoader } from '@components/AtgLoader/index';
import { A } from '@solidjs/router';
import { playlistsStore } from '@store/carriers';
import { isModalOpen } from '@store/modals/store';
import { addCarrierToPlaylist } from '@store/orders';
import { userStore } from '@store/user/store';
import { AccountMultipleIcon } from '@atgfr8/icons';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { Show, createEffect, createSignal } from 'solid-js';
import { getAndCheckPlaylists } from '@views/order/helpers';

import { Playlist } from '../../../../store/orders/types';
import { handleCarrierChange } from '../carrier/loads/LoadInfo/CarrierAutocomplete';
import cls from './classes';

export const SelectPlaylist = (props: {
  onClose: () => void;
  modalId: string;
  carrierId: number;
  tabIndex: number;
  load: ILoadEditViewModel;
  changeTrackingSource?: ChangeTrackingSource;
}) => {
  const [playlists, setPlaylists] = createSignal<Playlist[]>(
    playlistsStore.playlists,
  );
  const [selectedPlaylist, setSelectedPlaylist] = createSignal<Playlist>();
  const [loading, setLoading] = createSignal(false);

  createEffect(() => setPlaylists(playlistsStore.playlists));

  createEffect((wasOpen) => {
    const isOpen = isModalOpen(props.modalId);
    if (isOpen && !wasOpen && !loading()) {
      setLoading(true);
      void userStore.userPromise
        .then(() => getAndCheckPlaylists())
        .finally(() => setLoading(false));
    }
    return isOpen;
  }, false);

  const addToPlaylist = async () => {
    void (await addCarrierToPlaylist(
      {
        CarrierId: props.carrierId,
        PlaylistId: selectedPlaylist()!.id,
        Selected: true,
      },
      props.changeTrackingSource,
    ));
    props.onClose();
  };

  return (
    <Box>
      <Typography class={cls.playListModalTitle} variant="h6" component="h2">
        Select Playlist
      </Typography>
      <Show when={loading()}>
        <Box class="flex items-center justify-center min-h-[300px]">
          <AtgLoader />
        </Box>
      </Show>
      <Grid
        container
        class={loading() ? 'hidden' : ''}
        sx={{
          'align-items':
            selectedPlaylist() && playlists().length < 1
              ? 'center'
              : 'flex-start',
        }}
      >
        <Grid item size={Boolean(props.carrierId) ? 12 : 6}>
          <Grid
            container
            spacing={2}
            class="flex flex-col gap-[10px] items-center px-4 pt-6 cursor-pointer"
          >
            {playlists().length === 0 && (
              <Grid container sx={{ 'padding-bottom': '10px' }}>
                <Grid
                  item
                  size={10}
                  sx={{ 'padding-left': '20px', 'font-weight': 'bold' }}
                >
                  {' '}
                  You don't seem to have any playlists created.
                </Grid>
                <Grid
                  item
                  size={2}
                  sx={{
                    display: 'flex',
                    'justify-content': 'flex-end',
                    'padding-right': '20px',
                  }}
                >
                  <A href={carrierUrls.playLists()} target="_blank">
                    <Button
                      class={cls.btn}
                      variant="contained"
                      dataTestId="make-playlist-button"
                    >
                      MAKE ONE HERE!
                    </Button>
                  </A>
                </Grid>
              </Grid>
            )}
            <TextField
              label="Filter Playlist"
              placeholder="Filter Playlists"
              id="filter"
              size="md"
              style={{ width: '100%' }}
              onChange={(value: string) => {
                const filterValue = value.toLocaleLowerCase();
                const filteredList = playlistsStore.playlists.filter(
                  (playlists) =>
                    playlists.name.toLowerCase().includes(filterValue) ||
                    playlists.user.toLowerCase().includes(filterValue),
                );
                setPlaylists(filteredList);
              }}
              dataTestId="filter-playlist-text-input"
            />

            <Box style={{ width: '100%' }}>
              {playlists().map((value) => {
                return (
                  <>
                    <Box
                      class={
                        selectedPlaylist()?.id === value.id
                          ? cls.playListsContainerActive
                          : cls.playListsContainer
                      }
                      onClick={() => {
                        setSelectedPlaylist(value);
                        if (Boolean(props.carrierId)) {
                          void addToPlaylist();
                        }
                      }}
                    >
                      <AccountMultipleIcon class={cls.peopleIcon} />
                      <Box class={cls.playlistNameOwner}>
                        <Typography
                          variant="h6"
                          component="div"
                          style={{ 'word-break': 'break-word' }}
                        >
                          {value.name}
                        </Typography>
                        <Typography
                          variant="body1"
                          component="div"
                          class="opacity-60"
                        >
                          {`Owner: ${value.user} `}
                        </Typography>
                      </Box>
                      <Box class={cls.playlistGroupCount}>
                        <Typography variant="body1">
                          {value.carriers.length}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                );
              })}
            </Box>
          </Grid>
        </Grid>
        <Show when={!Boolean(props.carrierId)} fallback={<></>}>
          <Grid item size={6} sx={{ 'margin-bottom': '25px' }}>
            <div class={cls.carrierTable}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell />
                    <TableHeaderCell class="text-black">MC #</TableHeaderCell>
                    <TableHeaderCell class="text-black">DOT #</TableHeaderCell>
                    <TableHeaderCell class="text-black">Name</TableHeaderCell>
                    <TableHeaderCell class="text-black">Status</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedPlaylist()?.carriers.map((option) => (
                    <TableRow
                      class={cls.tableRow}
                      onClick={async () => {
                        props.onClose();
                        await handleCarrierChange(
                          option.id,
                          option.name!,
                          props.tabIndex,
                          props.load,
                        );
                      }}
                    >
                      <TableCell></TableCell>
                      <TableCell>{option.mc}</TableCell>
                      <TableCell>{option.dot}</TableCell>
                      <TableCell>{option.name}</TableCell>
                      <TableCell>{option.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Grid>
        </Show>
      </Grid>
      <Grid class="flex mt-2 justify-end gap-5">
        <Button
          class={cls.btn}
          onClick={() => props.onClose()}
          variant="text"
          dataTestId="select-playlist-cancel-button"
        >
          {'Cancel'}
        </Button>
      </Grid>
    </Box>
  );
};
